import {
  treeGroupId,
  type PathTree,
  type PathTreeNode,
} from "@/shared/tree/pathTree";

import type { InMemoryRows, InMemorySortKey } from "./useInMemoryRows";

/** Where a row sits in the tree: what `DataTable`'s `tree` prop draws. */
export interface TreeRowPlacement {
  kind: "group" | "leaf";
  /** 0-based level; the first cell is indented by it. */
  depth: number;
  /** The row's last path segment. */
  label: string;
  /** How many rows share the row's parent, and the row's 1-based place among them. */
  setSize: number;
  posInSet: number;
  /** A group's open state; always false on a leaf. */
  isExpanded: boolean;
  /**
   * A group's row repeated at the top of a page that starts inside the group
   * (see `pageTreeRows`); always false on a leaf.
   */
  continued: boolean;
}

/** A row standing for a path prefix that has rows beneath it. */
export interface TreeGroupRow<TLeaf = unknown> extends TreeRowPlacement {
  kind: "group";
  /** `treeGroupId(path)`, so a group never shares a leaf's id. */
  id: string;
  /** The prefix's segments joined with `.`. */
  path: string;
  /** The leaves anywhere beneath the group, for what its cells show. */
  leaves: readonly TLeaf[];
}

/** A row standing for one of the consumer's rows. */
export interface TreeLeafRow<TLeaf> extends TreeRowPlacement {
  kind: "leaf";
  /** The consumer's id for `row`. */
  id: string;
  row: TLeaf;
}

export type TreeRow<TLeaf> = TreeGroupRow<TLeaf> | TreeLeafRow<TLeaf>;

/** The id of the group's row where a page repeats it, so it never shares the group's own. */
export function continuedGroupId(path: string): string {
  return `continued:${treeGroupId(path)}`;
}

export interface BuildTreeRowsParams<TLeaf> {
  /** The tree to draw (`buildPathTree`). */
  tree: PathTree<TLeaf>;
  /** Whether the group at `path` (segments joined with `.`) is open. */
  isExpanded: (path: string) => boolean;
  /** Orders leaves within their parent. Omit to keep the tree's A-to-Z order. */
  sortKey?: InMemorySortKey<TLeaf>;
  /**
   * Orders groups within their parent by the leaves beneath each, alongside
   * `sortKey`, for a column that shows a value on groups too. Omit to keep
   * groups A to Z while the leaves sort.
   */
  groupSortKey?: InMemorySortKey<readonly TLeaf[]>;
  sortDirection?: "asc" | "desc";
}

type SortValue = ReturnType<InMemorySortKey<unknown>>;

const compareLabels = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/**
 * The rows of a tree table: the nodes of `tree` in order, each group followed
 * by its children while it is open.
 *
 * Unsorted, every level keeps the tree's order: A to Z, groups and leaves
 * together. With a `sortKey`, each level lists its groups and then its leaves
 * by the key, so the sort never pulls a leaf out of its group; the groups
 * stay A to Z, or go by `groupSortKey` when there is one.
 */
export function buildTreeRows<TLeaf>({
  tree,
  isExpanded,
  sortKey,
  groupSortKey,
  sortDirection = "asc",
}: BuildTreeRowsParams<TLeaf>): TreeRow<TLeaf>[] {
  const sorting = sortKey !== undefined || groupSortKey !== undefined;
  const factor = sortDirection === "asc" ? 1 : -1;
  const compareKeys = (keyA: SortValue, keyB: SortValue): number => {
    if (keyA === null || keyA === undefined) {
      return keyB === null || keyB === undefined ? 0 : 1;
    }
    if (keyB === null || keyB === undefined) {
      return -1;
    }
    if (typeof keyA === "string" && typeof keyB === "string") {
      return compareLabels(keyA, keyB) * factor;
    }
    return (Number(keyA) - Number(keyB)) * factor;
  };
  // The tree already lists each level A to Z, so a stable sort by the key
  // leaves ties in that order.
  const inOrder = (nodes: PathTreeNode<TLeaf>[]): PathTreeNode<TLeaf>[] => {
    if (!sorting) {
      return nodes;
    }
    const keys = new Map(
      nodes.map((node) => [
        node,
        node.kind === "leaf"
          ? sortKey?.(node.leaf)
          : groupSortKey?.(node.leaves),
      ]),
    );
    return [...nodes].sort((a, b) => {
      if (a.kind !== b.kind) {
        return a.kind === "group" ? -1 : 1;
      }
      return compareKeys(keys.get(a), keys.get(b));
    });
  };

  const rows: TreeRow<TLeaf>[] = [];
  const visit = (id: string) => {
    const children = inOrder(
      tree.getChildren(id).flatMap((childId) => tree.getNode(childId) ?? []),
    );
    children.forEach((node, index) => {
      const placement = {
        depth: node.depth,
        label: node.label,
        setSize: children.length,
        posInSet: index + 1,
        continued: false,
      };
      if (node.kind === "leaf") {
        rows.push({
          ...placement,
          kind: "leaf",
          id: node.id,
          row: node.leaf,
          isExpanded: false,
        });
        return;
      }
      const open = isExpanded(node.path);
      rows.push({
        ...placement,
        kind: "group",
        id: node.id,
        path: node.path,
        leaves: node.leaves,
        isExpanded: open,
      });
      if (open) {
        visit(node.id);
      }
    });
  };
  visit(tree.rootId);
  return rows;
}

/**
 * The tree's rows cut to the current page, with the groups the page starts
 * inside repeated at its top, outermost first and marked `continued`, so its
 * first rows read under their ancestors. Only the first row's ancestors are
 * needed: each group's rows follow it together, so any later row's parent is
 * on the page or among them. The repeats come on top of the page's
 * `pageSize` rows and are left out of `totalRowCount`, so pages start where
 * they would without them.
 */
export function pageTreeRows<TLeaf>(
  rows: readonly TreeRow<TLeaf>[],
  { currentPage, pageSize }: { currentPage: number; pageSize: number },
): InMemoryRows<TreeRow<TLeaf>> {
  const start = currentPage * pageSize;
  const end = start + pageSize;
  const page = rows.slice(start, end);

  // Each group above the first row is the nearest one before it that sits a
  // level higher than the last one found.
  const ancestors: TreeGroupRow<TLeaf>[] = [];
  let depth = page[0]?.depth ?? 0;
  for (let index = start - 1; index >= 0 && depth > 0; index--) {
    const row = rows[index];
    if (row.kind === "group" && row.depth < depth) {
      ancestors.push(row);
      depth = row.depth;
    }
  }
  const continued = ancestors.reverse().map((group): TreeGroupRow<TLeaf> => ({
    ...group,
    id: continuedGroupId(group.path),
    continued: true,
  }));

  return {
    visibleRows: [...continued, ...page],
    totalRowCount: rows.length,
    hasNextPage: end < rows.length,
  };
}
